import { BellIcon } from '@heroicons/react/24/outline';
import { CheckIcon, ChevronRightIcon, EnvelopeIcon, EyeIcon } from '@heroicons/react/24/solid';
import {
  Button,
  Card,
  CardBody,
  Divider,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollShadow,
  Spinner,
} from '@heroui/react';
import * as signalR from '@microsoft/signalr';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import { getAccessToken, useAuth } from '@/app/hooks/auth-provider.tsx';
import {
  useFetchAllNotificationMessagesForCurrentUserQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationsReadMutation,
} from '@/slices/notification-message-api-slice.ts';
import {
  useFetchAllOnboardImportsQuery,
  useFetchOnboardImportQuery,
} from '@/slices/onboard-import-api-slice.ts';
import type { NotificationMessage } from '../../types/notification-message.type.ts';
import { UserTypeEnum } from '../../types/user-type.ts';
import NotificationMessageAlert from './notification-message-alert.tsx';

const NOTIFICATION_DISPLAY_LIMIT = 50;

interface NotificationItemProps {
  notification: NotificationMessage;
  onPress: (notification: NotificationMessage) => void;
}

const NotificationItem = ({ notification, onPress }: NotificationItemProps) => (
  <Card
    isPressable
    radius='none'
    shadow='none'
    className='group w-full border-b border-zinc-100 bg-transparent text-left transition-colors hover:bg-primary-50/70 dark:border-zinc-800 dark:hover:bg-primary-950/30'
    onPress={() => onPress(notification)}
  >
    <CardBody className='flex-row items-start gap-3 px-4 py-3.5'>
      <span className='mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/40 dark:text-primary-300 dark:ring-primary-800'>
        <EnvelopeIcon className='size-3.5' />
      </span>
      <span className='min-w-0 flex-1'>
        <span className='flex items-center gap-1.5'>
          <span
            aria-hidden='true'
            className='size-1.5 shrink-0 rounded-full bg-primary-500 ring-2 ring-primary-100 dark:ring-primary-900'
          />
          <span className='sr-only'>Unread</span>
          <time
            dateTime={notification.sentAt}
            className='shrink-0 text-[0.7rem] tabular-nums text-zinc-400'
          >
            {formatDate(notification.sentAt, DateTime.DATETIME_SHORT)}
          </time>
        </span>
        <span
          className='mt-1.5 block text-sm leading-5 text-zinc-700 [&_a]:font-medium [&_a]:text-primary-600 [&_p]:m-0 dark:text-zinc-300'
          dangerouslySetInnerHTML={{ __html: notification.message }}
        />
      </span>
      <ChevronRightIcon className='mt-3 size-4 shrink-0 text-zinc-300 transition-colors group-hover:text-primary-500' />
    </CardBody>
  </Card>
);

const NotificationBadge = () => {
  const { user } = useAuth();
  const { data, isLoading, refetch } = useFetchAllNotificationMessagesForCurrentUserQuery({
    limit: NOTIFICATION_DISPLAY_LIMIT + 1,
    unreadOnly: true,
  });
  const { refetch: refetchAllOnboard } = useFetchAllOnboardImportsQuery();
  const [markAllNotificationsRead, { isLoading: isMarkingAllRead }] =
    useMarkAllNotificationsReadMutation();
  const [markNotificationsRead] = useMarkNotificationsReadMutation();
  const [notificationUi, setNotificationUi] = useState<{
    isPopoverOpen: boolean;
    openNotification: NotificationMessage | null;
  }>({
    isPopoverOpen: false,
    openNotification: null,
  });
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [importKey, setImportKey] = useState<string>();
  const { refetch: refetchSingleOnboard } = useFetchOnboardImportQuery(importKey ?? skipToken);
  const notifications = data?.slice(0, NOTIFICATION_DISPLAY_LIMIT) ?? [];
  const hasNotifications = notifications.length > 0;
  const hasMoreNotifications = (data?.length ?? 0) > NOTIFICATION_DISPLAY_LIMIT;
  const notificationCount = hasMoreNotifications
    ? `${NOTIFICATION_DISPLAY_LIMIT}+`
    : notifications.length;

  useEffect(() => {
    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${import.meta.env.VITE_BASE_API_URL}/api/hubs/notification-hub`, {
        accessTokenFactory: getAccessToken,
        withCredentials: false,
        transport: signalR.HttpTransportType.ServerSentEvents,
      })
      .withAutomaticReconnect()
      .build();

    setConnection(newConnection);
  }, []);

  useEffect(() => {
    if (connection && user) {
      // Only start if the connection is in Disconnected state
      if (connection.state === signalR.HubConnectionState.Disconnected) {
        connection
          .start()
          .then(() => {
            connection.on('ReceiveNotification', () => refetch());
            connection.on('OnboardImportNotification', importKey => {
              if (user?.userType === UserTypeEnum.Admin) {
                setImportKey(importKey);
                refetchAllOnboard();
                refetchSingleOnboard();
              }
              refetch();
            });
          })
          .catch(err => console.error('Error starting SignalR connection:', err));
      }
    }

    // Cleanup function
    return () => {
      if (connection && connection.state === signalR.HubConnectionState.Connected) {
        connection.off('ReceiveNotification');
        connection.off('OnboardImportNotification');
      }
    };
  }, [connection, user, refetch, refetchAllOnboard, refetchSingleOnboard]);

  useEffect(() => {
    return () => {
      // This cleanup function runs when the component unmounts
      if (connection) {
        connection.stop();
      }
    };
  }, []);

  const markAsRead = (value: NotificationMessage) => {
    markNotificationsRead([value.key]);
  };

  const showMessage = (value: NotificationMessage) => {
    if (!value.readAt) {
      markAsRead(value);
    }

    setNotificationUi({
      isPopoverOpen: false,
      openNotification: value,
    });
  };

  const closeMessage = () => {
    setNotificationUi(current => ({
      ...current,
      openNotification: null,
    }));
  };

  const markAllAsRead = () => {
    if (hasNotifications) {
      markAllNotificationsRead();
    }
  };

  return (
    <>
      <Popover
        isOpen={notificationUi.isPopoverOpen}
        placement='bottom-end'
        offset={12}
        onOpenChange={isPopoverOpen =>
          setNotificationUi(current => ({ ...current, isPopoverOpen }))
        }
        classNames={{
          content:
            'w-[calc(100vw-2rem)] max-w-[28rem] overflow-hidden border border-zinc-200 bg-white p-0 shadow-2xl shadow-zinc-950/15 dark:border-zinc-700 dark:bg-zinc-900',
        }}
      >
        <PopoverTrigger>
          <Button
            isIconOnly
            variant='light'
            radius='lg'
            aria-label={
              hasNotifications ? `${notificationCount} unread notifications` : 'Notifications'
            }
            className='relative size-7 min-w-7 overflow-visible border border-transparent p-0 text-zinc-500 transition hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700 data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-primary-500 dark:text-zinc-400 dark:hover:bg-primary-950/40 dark:hover:text-primary-300'
          >
            <BellIcon className='size-[22px]' />
            {!isLoading && hasNotifications && (
              <span className='absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border border-white bg-primary-500 px-1 text-[0.55rem] font-bold leading-none tabular-nums text-white dark:border-zinc-950'>
                {notificationCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent>
          <section className='w-full' aria-label='Notifications'>
            <header className='relative overflow-hidden border-b border-primary-200 bg-gradient-to-r from-primary-50 via-primary-100 to-primary-200 px-4 py-2.5 dark:border-zinc-700 dark:from-primary-950 dark:via-primary-900 dark:to-primary-800'>
              <span className='absolute -right-6 -top-10 size-24 rounded-full bg-primary-300/15 blur-2xl dark:bg-primary-600/15' />
              <div className='relative flex items-center justify-between gap-4'>
                <div>
                  <p className='text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-secondary-700 dark:text-secondary-400'>
                    Inbox
                  </p>
                  <h2 className='mt-0.5 font-serif text-base font-semibold text-zinc-950 dark:text-white'>
                    Notifications
                  </h2>
                </div>
                {!isLoading && hasNotifications && (
                  <span className='rounded-full border border-primary-200 bg-white/80 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-primary-700 shadow-sm backdrop-blur dark:border-primary-800 dark:bg-zinc-900/80 dark:text-primary-300'>
                    {notificationCount} unread
                  </span>
                )}
              </div>
            </header>

            <ScrollShadow className='max-h-[min(30rem,calc(100vh-13rem))]'>
              {isLoading && (
                <div className='flex min-h-48 flex-col items-center justify-center gap-3 px-6 py-12'>
                  <Spinner color='primary' size='md' />
                  <p className='text-sm text-zinc-500'>Loading notifications...</p>
                </div>
              )}

              {!isLoading && hasNotifications && (
                <div>
                  {notifications.map(item => (
                    <NotificationItem key={item.id} notification={item} onPress={showMessage} />
                  ))}
                </div>
              )}

              {!isLoading && !hasNotifications && (
                <Card radius='none' shadow='none' className='bg-transparent'>
                  <CardBody className='items-center px-6 py-12 text-center'>
                    <span className='flex size-12 items-center justify-center rounded-2xl bg-secondary-50 text-secondary-600 ring-1 ring-secondary-100 dark:bg-secondary-950/40 dark:text-secondary-400 dark:ring-secondary-900'>
                      <CheckIcon className='size-6' />
                    </span>
                    <p className='mt-4 font-serif text-base font-semibold text-zinc-900 dark:text-white'>
                      You&apos;re all caught up
                    </p>
                    <p className='mt-1 text-sm text-zinc-500'>No unread notifications right now.</p>
                  </CardBody>
                </Card>
              )}
            </ScrollShadow>

            <Divider />
            <footer className='grid grid-cols-2 gap-2 bg-zinc-50 p-3 dark:bg-zinc-900'>
              <Button
                as={Link}
                href='/notifications'
                color='primary'
                variant='solid'
                startContent={<EyeIcon className='size-4' />}
                onPress={() => setNotificationUi(current => ({ ...current, isPopoverOpen: false }))}
              >
                View all
              </Button>
              <Button
                variant='flat'
                color='primary'
                isDisabled={!hasNotifications}
                isLoading={isMarkingAllRead}
                startContent={!isMarkingAllRead && <EnvelopeIcon className='size-4' />}
                onPress={markAllAsRead}
              >
                Mark all read
              </Button>
            </footer>
          </section>
        </PopoverContent>
      </Popover>
      <NotificationMessageAlert
        onClose={closeMessage}
        isOpen={notificationUi.openNotification !== null}
        notification={notificationUi.openNotification}
      />
    </>
  );
};

export default NotificationBadge;
